import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Players } from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/format";
import { UserCircle, Trash, UserPlus } from "@phosphor-icons/react";

export default function PlayersPage() {
    const [players, setPlayers] = useState([]);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [q, setQ] = useState("");

    const load = useCallback(async () => setPlayers(await Players.list()), []);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        if (!name.trim() || !phone.trim()) { toast.error("Enter name and phone"); return; }
        await Players.create({ name: name.trim(), phone: phone.trim() });
        toast.success("Player saved");
        setName(""); setPhone(""); load();
    };
    const remove = async (id) => {
        if (!window.confirm("Delete player?")) return;
        await Players.remove(id); toast.success("Deleted"); load();
    };

    const filtered = players.filter(p =>
        !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.phone.includes(q)
    );

    return (
        <div className="space-y-8" data-testid="players-page">
            <div>
                <h2 className="font-serif text-4xl tracking-tight">Players</h2>
                <p className="text-muted-foreground text-sm mt-1">Master roster</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <Card className="card-lux lg:col-span-4">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <UserPlus weight="duotone" size={22} className="text-gold" />
                            <h3 className="font-serif text-xl">New player</h3>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <Label className="label-mini">Name</Label>
                                <Input data-testid="pl-input-name" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                            <div>
                                <Label className="label-mini">Phone (E.164)</Label>
                                <Input data-testid="pl-input-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1555..." />
                            </div>
                            <Button data-testid="pl-btn-add" onClick={add} className="btn-gold w-full h-11">Save</Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="card-lux lg:col-span-8">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4 gap-4">
                            <h3 className="font-serif text-xl">All players ({players.length})</h3>
                            <Input data-testid="pl-search" placeholder="Search by name or phone" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
                        </div>
                        <div className="divide-y divide-border/40" data-testid="pl-list">
                            {filtered.length === 0 && <div className="text-sm text-muted-foreground py-4">No players.</div>}
                            {filtered.map(p => (
                                <div key={p.id} className="flex items-center justify-between py-2.5">
                                    <div className="flex items-center gap-3">
                                        <UserCircle size={26} className="text-gold" weight="duotone" />
                                        <div>
                                            <div className="text-sm">{p.name}</div>
                                            <div className="text-xs text-muted-foreground font-mono-plex">{p.phone}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xs text-muted-foreground">{fmtTime(p.created_at)}</span>
                                        <Button data-testid={`pl-btn-del-${p.id}`} variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-destructive">
                                            <Trash size={18} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
